'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useEffect, useRef } from 'react'
import {
  GA_ENABLED,
  GA_SCRIPT_SRC,
  gaBootstrap,
  gaPageLocation,
  gaPageView,
  type GaSurface,
} from '@/lib/gtag'

// GA4 tag + SPA pageview loop.
//
// Mounted once per ROUTE GROUP — (marketing), (auth) and (app) each mount it
// with their own `surface`, and a request matches exactly one group, so exactly
// one instance is ever live. It is not in the root layout, even though that
// would be one mount instead of three: the root layout cannot know which group
// rendered, and `surface` is the only thing that tells the logged-out marketing
// home apart from the signed-in app home — `proxy.ts` serves both at `/`.
//
// `next/script` with afterInteractive is what loads gtag.js: Next moves it out
// of the hydration path, so the tag cannot delay interactivity the way a bare
// <script async> in <head> can.
//
// NOTE: no useSearchParams(). Campaign parameters are read off the location by
// lib/gtag (which filters them to an allowlist), so this component never needs
// the hook — which also keeps every static page from being opted into
// client-side rendering.
export function GoogleAnalytics({ surface }: { surface: GaSurface }) {
  const pathname = usePathname()
  // Previous in-app location, sanitised. document.referrer does not update on a
  // history-API navigation, so without this every SPA pageview would report the
  // referrer of the session's ENTRY page.
  const previous = useRef<string | null>(null)

  useEffect(() => {
    if (!GA_ENABLED) return
    gaBootstrap()
    // The address bar, not `pathname` — the campaign parameters live in the
    // query string, which usePathname() does not carry.
    const here = `${window.location.pathname}${window.location.search}`
    gaPageView(here, previous.current ?? document.referrer, surface)
    previous.current = gaPageLocation(window.location.origin, here, surface)
  }, [pathname, surface])

  if (!GA_ENABLED) return null

  return <Script id="ga-loader" src={GA_SCRIPT_SRC} strategy="afterInteractive" />
}
